import { Box, CircularProgress, Typography, useTheme, Skeleton } from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { ChatMessage } from './ChatMessage';
import { useRef, useEffect, useCallback, useMemo } from 'react';
import { useChatClient } from './useChatClient';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

const cookies = new Cookies();

// Skeleton for a single message
const MessageSkeleton = ({ isOwn }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  return (
    <Box className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
      {!isOwn && (
        <Skeleton
          variant="circular"
          width={32}
          height={32}
          sx={{ mr: 1, bgcolor: colors.card, flexShrink: 0 }}
        />
      )}
      <Box className={`flex flex-col ${isOwn ? 'items-end' : 'items-start'}`}>
        {!isOwn && (
          <Skeleton
            variant="text"
            width={80}
            height={14}
            sx={{ mb: 0.5, ml: 1, bgcolor: colors.card }}
          />
        )}
        <Skeleton
          variant="rounded"
          width={Math.random() * 100 + 150}
          height={40}
          sx={{
            bgcolor: isOwn ? `${colors.info}30` : colors.card,
            borderRadius: isOwn ? '16px 16px 4px 16px' : '16px 16px 16px 4px',
          }}
        />
      </Box>
    </Box>
  );
};

export const ChatMessageList = ({ messages, isLoading }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const { loadMoreMessages, hasMoreMessages } = useChatClient();
  const containerRef = useRef(null);
  const currentUserId = parseInt(cookies.get('userId'));
  const isInitialLoad = isLoading && messages.length === 0;

  const handleScroll = useCallback(() => {
    if (!containerRef.current || isLoading || !hasMoreMessages) return;

    const { scrollTop } = containerRef.current;
    if (scrollTop < 100) {
      loadMoreMessages();
    }
  }, [loadMoreMessages, isLoading, hasMoreMessages]);

  useEffect(() => {
    const container = containerRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [handleScroll]);

  const getTimeKey = (dateString) => {
    try {
      const date = new Date(dateString);
      return `${date.getHours()}:${date.getMinutes()}`;
    } catch {
      return '';
    }
  };

  const groupedMessages = useMemo(() => {
    const groups = {};
    messages.forEach((message) => {
      const date = new Date(message.createdAt).toLocaleDateString('de-DE', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
      if (!groups[date]) {
        groups[date] = [];
      }
      groups[date].push(message);
    });
    return groups;
  }, [messages]);

  // Show skeleton during initial load
  if (isInitialLoad) {
    return (
      <Box
        className="flex flex-col-reverse h-full overflow-hidden px-4 py-2 gap-3"
        sx={{
          '&::-webkit-scrollbar': {
            width: '4px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: colors.bgInput,
            borderRadius: '2px',
          },
        }}
      >
        {/* Skeleton messages */}
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
        <MessageSkeleton isOwn={false} />
        <MessageSkeleton isOwn={true} />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      className="flex flex-col-reverse h-full overflow-y-auto px-4 py-2 gap-1"
      sx={{
        // Keep overscroll inside the list so reaching the top/bottom doesn't chain
        // into an iOS visual-viewport pan of the page behind the chat.
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        '&::-webkit-scrollbar': {
          width: '4px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: 'transparent',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: colors.bgInput,
          borderRadius: '2px',
        },
      }}
    >
      {Object.entries(groupedMessages).map(([date, dateMessages]) => (
        <Box key={date} className="flex flex-col-reverse gap-1">
          {dateMessages.map((message, index) => {
            const isOwn = message.sender?.id === currentUserId;
            const prevMessage = dateMessages[index - 1];
            const showAvatar = !isOwn && (
              index === 0 ||
              prevMessage?.sender?.id !== message.sender?.id
            );

            // Show time only if this is the first message in a group
            // (different sender or different minute than the previous message)
            const showTime = index === 0 ||
              prevMessage?.sender?.id !== message.sender?.id ||
              getTimeKey(prevMessage?.createdAt) !== getTimeKey(message.createdAt);

            return (
              <ChatMessage
                key={message.id}
                message={message}
                isOwn={isOwn}
                showAvatar={showAvatar}
                showTime={showTime}
              />
            );
          })}

          <Box className="flex items-center justify-center my-3">
            <Box
              className="px-3 py-1 rounded-full"
              sx={{
                backgroundColor: colors.bgInput,
                color: colors.text,
                opacity: 0.6,
                fontSize: '0.7rem',
              }}
            >
              {date}
            </Box>
          </Box>
        </Box>
      ))}

      {isLoading && messages.length > 0 && (
        <Box className="flex justify-center py-4">
          <CircularProgress size={20} sx={{ color: colors.info }} />
        </Box>
      )}

      {!isLoading && messages.length === 0 && (
        <Box className="flex items-center justify-center h-full">
          <Typography variant="body2" sx={{ color: colors.text, opacity: 0.4, textAlign: 'center' }}>
            {t('No messages yet.')}<br />
            {t('Start the conversation!')}
          </Typography>
        </Box>
      )}
    </Box>
  );
};
